import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { patientsApi, patientAttendanceApi, assessmentsApi } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { isForbidden } from '../../utils/accessDenied';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlineEye,
  HiOutlinePhone,
  HiOutlineUsers,
  HiPencil,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineUser,
  HiOutlineExclamation
} from 'react-icons/hi';
import PatientForm from '../../components/Forms/PatientForm';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import {
  calculateDetailedAgeIST,
  formatMonthLongCalendarMonth,
  formatCalendarDateDMY,
  formatDateTimeDMYIST,
  getCalendarNowIST,
} from '../../utils/timezoneDisplay';

const Patients = () => {
  const { canRead, canCreate, canUpdate, isTherapist } = usePermissions();
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [therapyPlansData, setTherapyPlansData] = useState({}); // Store therapy plans by patient ID
  const [homePlanWarnings, setHomePlanWarnings] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'active',
    gender: '',
    doctor_id: '',
    date_from: '',
    date_to: '',
    unassigned_therapist: false,
    page: 1
  });
  const [pagination, setPagination] = useState({});
  const [unassignedTherapistCount, setUnassignedTherapistCount] = useState(0);

  const debouncedSearch = useDebouncedValue(filters.search, 300);

  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters.status, filters.gender, filters.doctor_id, filters.date_from, filters.date_to, filters.unassigned_therapist, filters.page, isTherapist]);

  useEffect(() => {
    fetchDoctors();
  }, []);

  // Fetch attendance data for current month (requires attendance read)
  useEffect(() => {
    if (!canRead('attendance') || isTherapist) {
      setAttendanceData({});
      return;
    }
    fetchAttendanceReport();
  }, [canRead, isTherapist]);

  const fetchAttendanceReport = async () => {
    if (!canRead('attendance')) return;
    try {
      const { month: currentMonth, year: currentYear } = getCalendarNowIST();
      const response = await patientAttendanceApi.getAttendanceReport({ 
        month: currentMonth, 
        year: currentYear 
      });
      if (response.data.success) {
        // Convert to a map for easy lookup by patient ID
        const attendanceMap = {};
        response.data.data.forEach(report => {
          attendanceMap[report.id] = {
            present: parseInt(report.present_days) || 0,
            absent: parseInt(report.absent_days) || 0,
            late: parseInt(report.late_days) || 0,
            excused: parseInt(report.excused_days) || 0,
          };
        });
        setAttendanceData(attendanceMap);
      }
    } catch (error) {
      if (isForbidden(error)) {
        setAttendanceData({});
        return;
      }
      console.error('Failed to fetch attendance report:', error);
    }
  };

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const params = {
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.gender ? { gender: filters.gender } : {}),
        ...(filters.doctor_id ? { doctor_id: filters.doctor_id } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        ...(filters.unassigned_therapist ? { unassigned_therapist: 1 } : {}),
        page: filters.page,
        limit: 20
      };
      const response = await patientsApi.getAll(params);
      const fetchedPatients = response.data.data.patients;
      setPatients(fetchedPatients);
      setPagination(response.data.data.pagination);
      setUnassignedTherapistCount(
        Number(response.data.data.unassigned_therapist_count) || 0
      );

      const plansMap = {};
      const warningMap = {};
      fetchedPatients.forEach((patient) => {
        plansMap[patient.id] = patient.therapy_plans || [];
        warningMap[patient.id] = patient.home_plan_warning || {
          hasPendingHomePlan: false,
          pendingCount: 0,
        };
      });
      setTherapyPlansData(plansMap);
      setHomePlanWarnings(isTherapist ? {} : warningMap);
    } catch (error) {
      toast.error('Failed to fetch patients');
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await assessmentsApi.getDoctorUsers();
      setDoctors(response.data.data || []);
    } catch (error) {
      setDoctors([]);
    }
  };

  // Get total days in current month
  const getDaysInCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  };

  const totalDaysInMonth = getDaysInCurrentMonth();
  const currentMonthName = formatMonthLongCalendarMonth(getCalendarNowIST().month);

  // Calculate attendance rate for a patient
  const getAttendanceInfo = (patientId) => {
    const data = attendanceData[patientId];
    if (!data) return null;
    const total = data.present + data.absent + data.late + data.excused;
    if (total === 0) return null;
    const rate = Math.round(((data.present + data.late) / total) * 100);
    return { ...data, total, rate, totalDaysInMonth };
  };

  const parseYmdToUtcDate = (value) => {
    if (!value) return null;
    const ymd = String(value).slice(0, 10);
    const parts = ymd.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
    return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  };

  const addDaysUtc = (date, days) => {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  };

  const buildWeekdayMask = (plan) => ({
    0: !!plan.sun,
    1: !!plan.mon,
    2: !!plan.tue,
    3: !!plan.wed,
    4: !!plan.thu,
    5: !!plan.fri,
    6: !!plan.sat,
  });

  const computeDueDateForPlan = (plan) => {
    const start = parseYmdToUtcDate(plan.start_date);
    if (!start) return null;
    const targetSessions = Number(plan.target_sessions) > 0 ? Number(plan.target_sessions) : 90;
    const weekdayMask = buildWeekdayMask(plan);
    const hasAnyDay = Object.values(weekdayMask).some(Boolean);
    if (!hasAnyDay) return null;

    let sessionsLeft = targetSessions;
    let cursor = start;
    while (sessionsLeft > 0) {
      if (weekdayMask[cursor.getUTCDay()]) sessionsLeft -= 1;
      if (sessionsLeft === 0) return cursor;
      cursor = addDaysUtc(cursor, 1);
    }
    return null;
  };

  // Calculate next appointment days based on configured plan type session-days
  /** Active therapy type names for therapist list (matches PatientDetail plan display). */
  const getActiveTherapyNames = (patientId) => {
    const plans = therapyPlansData[patientId] || [];
    const active = plans.filter((p) => p.status === 'active');
    if (active.length === 0) return '—';
    const names = [];
    const seen = new Set();
    active.forEach((plan) => {
      if (Array.isArray(plan.therapy_types) && plan.therapy_types.length > 0) {
        plan.therapy_types.forEach((t) => {
          const n = (t && t.name) || '';
          if (n && !seen.has(n)) {
            seen.add(n);
            names.push(n);
          }
        });
      } else if (plan.therapy_type && !seen.has(plan.therapy_type)) {
        seen.add(plan.therapy_type);
        names.push(plan.therapy_type);
      }
    });
    if (names.length > 0) return names.join(', ');
    const planTypes = active.map((p) => p.plan_type).filter(Boolean);
    return planTypes.length ? planTypes.join(', ') : '—';
  };

  const getNextAppointmentDays = (patientId) => {
    const plans = therapyPlansData[patientId] || [];
    
    if (plans.length === 0) {
      return null;
    }

    // Filter only active plans
    const activePlans = plans.filter(plan => plan.status === 'active');
    
    if (activePlans.length === 0) {
      return null;
    }

    const withStartDate = activePlans
      .filter((plan) => !!plan.start_date)
      .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
    if (withStartDate.length === 0) {
      return null;
    }
    const selectedPlan = withStartDate[0];
    const nextAppointmentDate = computeDueDateForPlan(selectedPlan);
    if (!nextAppointmentDate) return null;

    const nowIst = getCalendarNowIST();
    const today = new Date(Date.UTC(nowIst.year, nowIst.month - 1, nowIst.day));
    const diffDays = Math.ceil((nextAppointmentDate - today) / (1000 * 60 * 60 * 24));

    return {
      days: diffDays,
      date: nextAppointmentDate,
      startDate: parseYmdToUtcDate(selectedPlan.start_date),
    };
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
    fetchPatients();
  };

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value, page: 1 });
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.search ||
    filters.status ||
    filters.gender ||
    filters.doctor_id ||
    filters.date_from ||
    filters.date_to ||
    filters.unassigned_therapist;

  const viewUnassignedPatients = () => {
    setFilters((prev) => ({
      ...prev,
      unassigned_therapist: true,
      status: prev.status || 'active',
      page: 1,
    }));
  };

  const clearUnassignedFilter = () => {
    setFilters((prev) => ({
      ...prev,
      unassigned_therapist: false,
      page: 1,
    }));
  };

  const handleCreate = () => {
    setSelectedPatient(null);
    setShowForm(true);
  };

  const handleEdit = async (patient) => {
    try {
      const response = await patientsApi.getById(patient.id);
      setSelectedPatient(response.data.data);
      setShowForm(true);
    } catch (error) {
      toast.error('Failed to load patient details');
    }
  };

  const handleFormSuccess = () => {
    fetchPatients();
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-gray-900">Patients</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isTherapist ? 'Patients assigned to you' : 'Manage patient records and therapy plans'}
          </p>
        </div>
        {!isTherapist && canCreate('patients') && (
          <div className="flex gap-2">
            <Link to="/leads" className="btn btn-secondary">
              <HiOutlinePlus className="w-5 h-5 mr-2" />
              Add from Lead
            </Link>
          </div>
        )}
      </div>

      {/* Filters (hidden for therapist) */}
      {!isTherapist && (
        <div className="card animate-slide-down overflow-hidden p-2.5">
          <form className="space-y-1.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {/* Search Field */}
              <div className="relative sm:col-span-2 lg:col-span-1">
                <label className="block text-[10px] text-gray-500 mb-0.5">Search</label>
                <div className="relative">
                  <HiOutlineSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none z-10" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Search by name, code, phone..."
                    className="input h-8 pl-7 w-full text-[11px]"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="w-full">
                <label className="block text-[10px] text-gray-500 mb-0.5">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input h-8 w-full text-[11px]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="discharged">Discharged</option>
                </select>
              </div>

              {/* Gender Filter */}
              <div className="w-full">
                <label className="block text-[10px] text-gray-500 mb-0.5">Gender</label>
                <select
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                  className="input h-8 w-full text-[11px]"
                >
                  <option value="">All Genders</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Doctor Filter */}
              <div className="w-full">
                <label className="block text-[10px] text-gray-500 mb-0.5">Doctor</label>
                <select
                  value={filters.doctor_id}
                  onChange={(e) => handleFilterChange('doctor_id', e.target.value)}
                  className="input h-8 w-full text-[11px]"
                >
                  <option value="">All Doctors</option>
                  {doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range Picker */}
              <div className="w-full sm:col-span-2 lg:col-span-1">
                <label className="block text-[10px] text-gray-500 mb-0.5">Enrollment Date Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="input h-8 flex-1 min-w-0 text-[11px]"
                  />
                  <span className="text-gray-400 text-xs font-medium flex-shrink-0">to</span>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="input h-8 flex-1 min-w-0 text-[11px]"
                    min={filters.date_from || undefined}
                  />
                </div>
              </div>
            </div>

          </form>
        </div>
      )}

      {!isTherapist && (unassignedTherapistCount > 0 || filters.unassigned_therapist) && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
          <div className="flex items-start gap-2 text-sm text-amber-900">
            <HiOutlineExclamation className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p>
              {unassignedTherapistCount === 1
                ? '1 patient has no therapist assigned'
                : `${unassignedTherapistCount} patients have no therapist assigned`}
              {filters.unassigned_therapist ? ' — showing unassigned only.' : '.'}
            </p>
          </div>
          {filters.unassigned_therapist ? (
            <button
              type="button"
              onClick={clearUnassignedFilter}
              className="text-sm font-medium text-amber-800 underline underline-offset-2 hover:text-amber-950 shrink-0"
            >
              Clear filter
            </button>
          ) : (
            <button
              type="button"
              onClick={viewUnassignedPatients}
              className="text-sm font-medium text-amber-800 underline underline-offset-2 hover:text-amber-950 shrink-0"
            >
              View unassigned
            </button>
          )}
        </div>
      )}

      {/* Patients grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading && !initialLoaded ? (
          <div className="col-span-full flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : patients.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            <HiOutlineUsers className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>No patients found</p>
          </div>
        ) : (
          patients.map((patient) => {
            const attendance = getAttendanceInfo(patient.id);
            const homePlanWarning = homePlanWarnings[patient.id];
            const assignedTherapists = Array.isArray(patient.assigned_therapists)
              ? patient.assigned_therapists
              : [];
            const visibleTherapists = assignedTherapists;

            if (isTherapist) {
              const genderLabel = patient.gender
                ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1)
                : '—';
              const doctorsLine =
                visibleTherapists.length > 0
                  ? visibleTherapists.map((t) => t.full_name).join(', ')
                  : '—';
              const therapyLine = getActiveTherapyNames(patient.id);

              return (
                <Link
                  key={patient.id}
                  to={`/patients/${patient.id}`}
                  className="card block p-4 transition-shadow hover:shadow-md"
                >
                  <h2 className="text-base font-semibold text-gray-900">{patient.full_name}</h2>
                  <dl className="mt-3 space-y-2.5 text-sm">
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Gender</dt>
                      <dd className="mt-0.5 text-gray-800">{genderLabel}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Doctors / therapists</dt>
                      <dd className="mt-0.5 text-gray-800">{doctorsLine}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">Therapy</dt>
                      <dd className="mt-0.5 text-gray-800">{therapyLine}</dd>
                    </div>
                  </dl>
                </Link>
              );
            }

            return (
              <div
                key={patient.id}
                className="card p-4 hover:shadow-md transition-shadow relative"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm text-gray-900">{patient.full_name}</p>
                    <p className="text-xs text-primary-600">{patient.patient_code}</p>
                  </div>
                  <span className={`badge ${
                      patient.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : patient.status === 'discharged'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {patient.status}
                    </span>
                </div>

                <div className="mt-3 space-y-1.5 text-xs text-gray-500">
                  {patient.status === 'inactive' && patient.inactive_at && (
                    <p className="text-amber-700 font-medium">
                      Inactive since {formatDateTimeDMYIST(patient.inactive_at)}
                    </p>
                  )}
                  {patient.gender && (
                    <p>
                      {patient.dob && calculateDetailedAgeIST(patient.dob)
                        ? `${calculateDetailedAgeIST(patient.dob)} • `
                        : ''}
                      {patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1)}
                    </p>
                  )}
                  {!isTherapist && (
                    <div className="flex items-center gap-2">
                      <HiOutlinePhone className="w-4 h-4" />
                      {patient.contact_primary}
                    </div>
                  )}
                  {!isTherapist && patient.guardian_name && (
                    <p>Guardian: {patient.guardian_name}</p>
                  )}
                  {visibleTherapists.length > 0 ? (
                    <div className="flex items-center gap-2 pt-1">
                      <HiOutlineUser className="w-4 h-4 text-primary-600" />
                      <span className="font-medium text-gray-700">
                        {visibleTherapists.map((therapist) => therapist.full_name).join(', ')}
                      </span>
                    </div>
                  ) : (
                    <div className="pt-1">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                        <HiOutlineExclamation className="w-3 h-3" />
                        No therapist assigned
                      </span>
                    </div>
                  )}
                </div>

                {homePlanWarning?.hasPendingHomePlan && (
                  <div className="mt-2.5">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                      <HiOutlineExclamation className="w-3 h-3" />
                      Home plan pending ({homePlanWarning.pendingCount})
                    </span>
                  </div>
                )}

                {/* Next Appointment Days */}
                {(() => {
                  const nextAppointment = getNextAppointmentDays(patient.id);
                  if (nextAppointment) {
                    const isOverdue = nextAppointment.days < 0;
                    const isDueSoon = nextAppointment.days >= 0 && nextAppointment.days <= 7;
                    return (
                      <div className="mt-2.5 pt-2.5 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Next Appointment
                          </span>
                          <span className={`text-xs font-semibold ${
                            isOverdue 
                              ? 'text-red-600' 
                              : isDueSoon 
                              ? 'text-amber-600' 
                              : 'text-blue-600'
                          }`}>
                            {isOverdue 
                              ? `${Math.abs(nextAppointment.days)} days overdue`
                              : nextAppointment.days === 0
                              ? 'Today'
                              : `${nextAppointment.days} day${nextAppointment.days !== 1 ? 's' : ''} remaining`
                            }
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {formatCalendarDateDMY(nextAppointment.date)}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Attendance Stats */}
                {attendance && (
                  <div className="mt-3 pt-2.5 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {currentMonthName} Attendance
                      </span>
                      <span className={`text-xs font-semibold ${
                        attendance.rate >= 80 ? 'text-emerald-600' :
                        attendance.rate >= 60 ? 'text-amber-600' :
                        'text-rose-600'
                      }`}>
                        {attendance.rate}%
                      </span>
                    </div>
                    {/* Progress Bar */}
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex mb-1.5">
                      <div 
                        className="bg-emerald-500 h-full" 
                        style={{ width: `${(attendance.present / totalDaysInMonth) * 100}%` }}
                      />
                      <div 
                        className="bg-amber-500 h-full" 
                        style={{ width: `${(attendance.late / totalDaysInMonth) * 100}%` }}
                      />
                      <div 
                        className="bg-sky-500 h-full" 
                        style={{ width: `${(attendance.excused / totalDaysInMonth) * 100}%` }}
                      />
                      <div 
                        className="bg-rose-500 h-full" 
                        style={{ width: `${(attendance.absent / totalDaysInMonth) * 100}%` }}
                      />
                    </div>
                    {/* Stats Row */}
                    <div className="flex items-center justify-between text-[11px]">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <HiOutlineCheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-gray-600">{attendance.present}P</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <HiOutlineXCircle className="w-3.5 h-3.5 text-rose-500" />
                          <span className="text-gray-600">{attendance.absent}A</span>
                        </div>
                        {attendance.late > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            <span className="text-gray-600">{attendance.late}L</span>
                          </div>
                        )}
                        {attendance.excused > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-sky-500"></span>
                            <span className="text-gray-600">{attendance.excused}E</span>
                          </div>
                        )}
                      </div>
                      <span className="text-gray-400 font-medium">
                        {attendance.total}/{totalDaysInMonth} days
                      </span>
                    </div>
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      Enrolled: {formatCalendarDateDMY(patient.enrollment_date)}
                    </span>
                    <div className="flex items-center gap-2">
                      {canUpdate('patients') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(patient);
                          }}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Edit Patient"
                        >
                          <HiPencil className="w-4 h-4" />
                        </button>
                      )}
                      {!isTherapist && (
                        <Link
                          to={`/patients/${patient.id}`}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-50 rounded-lg"
                          title="View Details"
                        >
                          <HiOutlineEye className="w-5 h-5" />
                        </Link>
                      )}
                    </div>
                  </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            disabled={pagination.page === 1}
            className="btn btn-secondary disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            disabled={pagination.page >= pagination.totalPages}
            className="btn btn-secondary disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Patient Form Modal */}
      {showForm && (
        <PatientForm
          patient={selectedPatient}
          onClose={() => {
            setShowForm(false);
            setSelectedPatient(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default Patients;

